import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useOrg } from "../auth/OrgProvider";
import { Card, CardHeader } from "../ui/Card";
import type { Service } from "@lawnops/shared";

export function ServicesPage() {
  const { t } = useTranslation("services");
  const { activeOrgId } = useOrg();

  const { data, isLoading, error } = useQuery({
    queryKey: ["services", activeOrgId],
    enabled: Boolean(supabase && activeOrgId),
    queryFn: async (): Promise<Service[]> => {
      if (!supabase || !activeOrgId) return [];
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("organization_id", activeOrgId)
        .is("archived_at", null)
        .order("name");
      if (error) throw error;
      return (data ?? []) as Service[];
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("title")}</h1>
        <p className="text-sm text-slate-500">{t("subtitle")}</p>
      </div>
      <Card>
        <CardHeader title={t("listTitle")} />
        {!supabase ? (
          <EmptyState message={t("supabaseEmpty")} />
        ) : isLoading ? (
          <EmptyState message={t("loading")} />
        ) : error ? (
          <EmptyState message={`${t("errorPrefix")}${(error as Error).message}`} />
        ) : data && data.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2">{t("columns.name")}</th>
                <th className="py-2">{t("columns.basePrice")}</th>
                <th className="py-2">{t("columns.perSqft")}</th>
                <th className="py-2">{t("columns.estMinutes")}</th>
                <th className="py-2">{t("columns.weatherSensitive")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((s) => (
                <tr key={s.id}>
                  <td className="py-3 font-medium text-slate-900">{s.name}</td>
                  <td className="py-3 text-slate-700">${s.base_price.toFixed(2)}</td>
                  <td className="py-3 text-slate-700">${s.price_per_sqft.toFixed(4)}</td>
                  <td className="py-3 text-slate-700">{s.estimated_minutes ?? "—"}</td>
                  <td className="py-3 text-slate-700">
                    {s.weather_sensitive ? t("yes") : t("no")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState message={t("empty")} />
        )}
      </Card>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="py-6 text-center text-sm text-slate-500">{message}</p>;
}
